import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getVendorById } from "../../api/services/vendorService";
import DonateLeftBanner from "../../components/common/DonateLeftBanner";
import AuthenticationTextInput from "../../components/common/TextInput/AuthenticationTextInput";
import StripeEmbeddedCheckout from "../../components/extendable/StripeEmbeddedCheckout";
import { handleDonate } from "../../utils/Donation";
import {
  sanitizeContactInput,
  validateContactPhone,
} from "../../utils/phoneValidation";
import { canViewPrivateCampaign } from "../../utils/authUtils";
import { Countries } from "../../utils/GeoLocation/Country";
import { StatesCountry } from "../../utils/GeoLocation/States";
import { convertInrToCurrency, formatConvertedAmount } from "../../utils/vendorCurrencyConversion";
import { setStripeCheckout, clearStripeCheckout } from "../../store/slices/stripeCheckoutSlice";
import { waitForFinalStatus } from "../../utils/Stripe";
import { Images } from "../../utils/Images";

const currencyList = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "AED", symbol: "د.إ" },
  { code: "CAD", symbol: "C$" },
  { code: "AUD", symbol: "A$" },
  { code: "GBP", symbol: "£" },
];

const fieldSelectStyle = (isMobile, hasError) => ({
  width: "100%",
  boxSizing: "border-box",
  background: "#fff",
  borderRadius: "8px",
  fontWeight: 500,
  fontSize: isMobile ? "12px" : "14px",
  border: hasError ? "1px solid #F04951" : "1px solid #B9B9B8",
  height: isMobile ? "43px" : "52px",
  padding: "0 40px 0 14px",
  color: "#222",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  outline: "none",
  cursor: "pointer",
});

const parsePrice = (value) => Number(String(value || "0").replace(/[^\d.]/g, "")) || 0;
const sanitizeUrl = (url) => String(url || "").replace(/%22$/i, "").replace(/"+$/, "");

const getSupportOptions = (amount, currencySymbol = "₹") => {
  if (!amount || Number(amount) <= 0) {
    return [
      { label: `12% - ${currencySymbol}0`, value: "12" },
      { label: `15% - ${currencySymbol}0`, value: "15" },
      { label: `20% - ${currencySymbol}0`, value: "20" },
    ];
  }
  const donationAmount = Number(amount);
  return [
    { label: `12% - ${currencySymbol}${Math.round(donationAmount * 0.12)}`, value: "12" },
    { label: `15% - ${currencySymbol}${Math.round(donationAmount * 0.15)}`, value: "15" },
    { label: `20% - ${currencySymbol}${Math.round(donationAmount * 0.20)}`, value: "20" },
  ];
};

const CustomDonateButton = styled(Button)(({ isMobile, isTablet }) => ({
  backgroundColor: "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",
  width: "100%",
  height: isMobile ? "44px" : isTablet ? "48px" : "52px",
  fontWeight: 700,
  fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px",
  lineHeight: isMobile ? "13px" : isTablet ? "14px" : "15px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:disabled": {
    backgroundColor: "#d63f46",
    opacity: 0.7,
    cursor: "not-allowed",
    color: "#FFFFFF",
  },
}));

const SmallVendorsPaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { clientSecret: stripeClientSecret, sessionId: stripeSessionId, details: stripeDetails } =
    useSelector((state) => state.stripeCheckout || {});
  const isMobile = useMediaQuery("(max-width: 767.95px)");
  const isSmallScreen = useMediaQuery("(max-width: 899px)");
  const isMediumScreen = useMediaQuery("(min-width: 900px) and (max-width: 1200px)");
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [supportBy, setSupportBy] = useState("12");
  const [currency, setCurrency] = useState(currencyList[0]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postal: "",
    countryId: "",
    country: "",
    state: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [whatsapp, setWhatsapp] = useState(true);
  const [anonymousCheck, setAnonymousCheck] = useState(false);
  const [isIndianCitizen, setIsIndianCitizen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState("");
  const isClosedVendor = Number(vendor?.closed_vendor) === 1;
  const isPrivateVendor = Number(vendor?.is_private) === 1;
  const canAccessPrivateVendor = canViewPrivateCampaign(user, isAuthenticated);
  const isPrivateVendorBlocked = isPrivateVendor && !canAccessPrivateVendor;
  const isUnavailableVendor = isClosedVendor || isPrivateVendorBlocked;

  useEffect(() => {
    let mounted = true;
    const fetchVendor = async () => {
      try {
        setLoading(true);
        const response = await getVendorById(id);
        const detail = response?.data || response;
        if (!mounted) return;
        const activeVendor = detail && (detail.status === 1 || detail.status === true) ? detail : null;
        setVendor(activeVendor);
      } catch (error) {
        if (!mounted) return;
        setVendor(null);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };
    if (id) fetchVendor();
    return () => {
      mounted = false;
    };
  }, [id]);

  const products = useMemo(() => {
    const section = Array.isArray(vendor?.vendorProducts) ? vendor.vendorProducts[0] : null;
    const items = Array.isArray(section?.products) ? section.products : [];
    return items.map((item, index) => ({
      id: item?.id || `product-${index}`,
      title: item?.title || "Product",
      image: sanitizeUrl(item?.image),
      unitPrice: parsePrice(item?.price),
    }));
  }, [vendor]);

  useEffect(() => {
    const initial = products.reduce((acc, item) => {
      acc[item.id] = 0;
      return acc;
    }, {});
    setQuantities(initial);
  }, [products]);

  const totalQty = useMemo(() => Object.values(quantities).reduce((sum, qty) => sum + (Number(qty) || 0), 0), [quantities]);
  const productTotal = useMemo(
    () => products.reduce((sum, item) => sum + item.unitPrice * (quantities[item.id] || 0), 0),
    [products, quantities]
  );
  const supportOptions = useMemo(() => getSupportOptions(productTotal, "₹"), [productTotal]);
  const tipPercentage = Number(supportBy) || 12;
  const tip = Math.round(productTotal * (tipPercentage / 100));
  const isInrPayment = currency.code === "INR";
  const inrTotalWithTip = productTotal + tip;
  const inrOverseasBase = productTotal;

  const filteredStates = form.countryId
    ? StatesCountry.filter((state) => state.countryId === parseInt(form.countryId, 10))
    : [];

  useEffect(() => {
    if (!isInrPayment && inrOverseasBase > 0) {
      let cancelled = false;
      const run = async () => {
        setConversionLoading(true);
        setConversionError("");
        try {
          const result = await convertInrToCurrency(inrOverseasBase, currency.code);
          if (!cancelled) {
            setConvertedAmount(result.amount);
          }
        } catch (err) {
          if (!cancelled) {
            setConvertedAmount(null);
            setConversionError(err?.message || "Could not convert amount.");
          }
        } finally {
          if (!cancelled) setConversionLoading(false);
        }
      };
      run();
      return () => {
        cancelled = true;
      };
    }
    setConvertedAmount(null);
    setConversionError("");
    return undefined;
  }, [isInrPayment, inrOverseasBase, currency.code]);

  useEffect(() => () => dispatch(clearStripeCheckout()), [dispatch]);

  useEffect(() => {
    if (isInrPayment) {
      dispatch(clearStripeCheckout());
    }
  }, [isInrPayment, dispatch]);

  const updateQty = (productId, delta) => {
    setQuantities((prev) => {
      const next = Math.max(0, (prev[productId] || 0) + delta);
      return { ...prev, [productId]: next };
    });
  };

  const handleInputChange = (field) => (e) => {
    const value =
      field === "phone"
        ? sanitizeContactInput(e.target.value, { indianOnly: isInrPayment })
        : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    const selected = Countries.find((c) => c.id === parseInt(countryId, 10));
    setForm((prev) => ({
      ...prev,
      countryId,
      country: selected?.code || "",
      state: "",
    }));
    if (selected?.currency) {
      const match = currencyList.find((c) => c.code === selected.currency);
      if (match && match.code !== "INR") {
        setCurrency(match);
      }
    }
    if (formErrors.country) {
      setFormErrors((prev) => ({ ...prev, country: "" }));
    }
  };

  const handleStateChange = (e) => {
    const stateName = e.target.value;
    const selectedState = filteredStates.find((s) => s.name === stateName);
    setForm((prev) => ({
      ...prev,
      state: selectedState?.code || stateName,
    }));
    if (formErrors.state) {
      setFormErrors((prev) => ({ ...prev, state: "" }));
    }
  };

  const getStripeCountryState = () => {
    const countryEntry = Countries.find((c) => c.id === parseInt(form.countryId, 10));
    return {
      country: countryEntry?.code || form.country || "",
      state: form.state || "",
    };
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name?.trim()) errors.name = "Full name is required";
    if (!form.email?.trim()) errors.email = "Email is required";
    if (!form.postal?.trim()) errors.postal = "Postal code is required";

    const phoneCheck = validateContactPhone(form.phone, { indianOnly: isInrPayment });
    if (!phoneCheck.isValid) errors.phone = phoneCheck.error;

    if (!isInrPayment) {
      if (!form.countryId) errors.country = "Country is required";
      if (form.countryId && filteredStates.length > 0 && !form.state) {
        errors.state = "State is required";
      }
      if (conversionError || convertedAmount == null) {
        errors.conversion = conversionError || "Please wait for currency conversion.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onStripeComplete = useCallback(
    async (result) => {
      const sessionId = result?.id || stripeSessionId;
      const now = new Date();
      const formattedDate = now.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      const formattedTime = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const paymentDateTime = `${formattedDate} | ${formattedTime}`;

      try {
        const summary = await waitForFinalStatus(sessionId);
        const data = {
          paymentId: summary.paymentId || null,
          sessionId: summary.sessionId,
          amount: summary.amount ?? stripeDetails?.amount,
          currency: (summary.currency || stripeDetails?.currency || "").toUpperCase(),
          paymentMethod: "Stripe",
          paymentDateTime,
          formattedDate,
          formattedTime,
          donationType: "vendor",
          vendorId: vendor?.id,
        };

        if (summary.status === "success") {
          navigate("/thankyou-Page", {
            state: { status: "Success", data, details: stripeDetails },
          });
        } else {
          navigate("/payment-failure", {
            state: { status: "Failed", data, details: stripeDetails },
          });
        }
      } catch {
        navigate("/payment-failure", {
          state: {
            status: "Failed",
            data: {
              sessionId,
              amount: stripeDetails?.amount,
              currency: stripeDetails?.currency,
              donationType: "vendor",
              vendorId: vendor?.id,
            },
            details: stripeDetails,
          },
        });
      } finally {
        dispatch(clearStripeCheckout());
      }
    },
    [stripeSessionId, stripeDetails, navigate, dispatch, vendor?.id]
  );

  const handleDonateClick = async () => {
    if (totalQty === 0 || submitting || isUnavailableVendor || stripeClientSecret) return;
    if (!validateForm()) return;

    const phoneCheck = validateContactPhone(form.phone, { indianOnly: isInrPayment });
    const contact = isInrPayment ? phoneCheck.contact || phoneCheck.digits : phoneCheck.contact;
    const { country, state } = getStripeCountryState();

    setSubmitting(true);
    try {
      if (isInrPayment) {
        await handleDonate(
          {
            name: form.name,
            email: form.email,
            phone: contact,
            postal: form.postal,
          },
          Number(inrTotalWithTip),
          navigate,
          {
            currency: "INR",
            country: "IN",
            state: "KA",
            donationType: "Vendor",
            vendorId: Number(vendor?.id),
            vendorName: vendor?.title || vendor?.cart || "",
            vendor_name: vendor?.title || vendor?.cart || "",
            tipAmount: Number(tip) || 0,
            anonymousCheck: anonymousCheck ? 1 : 0,
          },
          user?.id
        );
        return;
      }

      const payAmount = Number(convertedAmount);
      const stripeResult = await handleDonate(
        {
          name: form.name,
          email: form.email,
          phone: contact,
          postal: form.postal,
        },
        payAmount,
        navigate,
        {
          currency: currency.code,
          country,
          state,
          donationType: "vendor",
          vendorId: Number(vendor?.id),
          vendorName: vendor?.title || vendor?.cart || "",
          vendor_name: vendor?.title || vendor?.cart || "",
          tipAmount: 0,
          anonymousCheck: anonymousCheck ? 1 : 0,
          contact,
        },
        user?.id
      );

      if (stripeResult?.clientSecret && stripeResult?.sessionId) {
        let decodedClientSecret = stripeResult.clientSecret;
        if (decodedClientSecret?.includes("%")) {
          try {
            decodedClientSecret = decodeURIComponent(decodedClientSecret);
          } catch {
            /* keep raw */
          }
        }
        dispatch(
          setStripeCheckout({
            clientSecret: decodedClientSecret,
            sessionId: stripeResult.sessionId,
            details: {
              name: form.name,
              email: form.email,
              phone: contact,
              amount: payAmount,
              currency: currency.code,
              donationType: "vendor",
              vendorId: Number(vendor?.id),
              vendorName: vendor?.title || vendor?.cart || "",
              country,
              state,
              inrAmount: inrOverseasBase,
            },
          })
        );
      } else {
        alert("Could not start Stripe checkout. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const donateButtonLabel = () => {
    if (isUnavailableVendor) {
      return isClosedVendor ? "Vendor Closed" : "Donate";
    }
    if (submitting) return null;
    if (isInrPayment) {
      return `Donate ₹${inrTotalWithTip.toLocaleString("en-IN")}`;
    }
    if (conversionLoading) return "Converting…";
    if (convertedAmount != null) {
      return `Donate ${formatConvertedAmount(convertedAmount, currency.code)}`;
    }
    return `Donate ${currency.symbol}—`;
  };

  if (loading) {
    return (
      <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto", px: "16px", py: "40px" }}>
        <Typography sx={{ fontSize: "16px", color: "#686866" }}>Loading payment details...</Typography>
      </Box>
    );
  }

  if (!vendor) {
    return (
      <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto", px: "16px", py: "40px" }}>
        <Typography sx={{ fontSize: "16px", color: "#686866" }}>Vendor payment details are unavailable.</Typography>
      </Box>
    );
  }

  if (isPrivateVendorBlocked) {
    return (
      <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto", px: "16px", py: "40px" }}>
        <Typography sx={{ fontSize: "16px", color: "#686866" }}>Vendor payment details are unavailable.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          background: isMobile ? "unset" : "rgba(232, 232, 232, 0.40)",
          p: isMobile ? "0" : isMediumScreen ? "16px" : "30px",
          width: "94.4%",
          maxWidth: "1440px",
          borderRadius: "20px",
          mx: "auto",
          mb: isSmallScreen ? "90px" : "40px",
        }}
      >
        <Box sx={{ display: "flex", gap: isSmallScreen ? "0" : "24px" }}>
          <Box sx={{ display: isSmallScreen ? "none" : "block" }}>
            <DonateLeftBanner />
          </Box>

          <Box
            sx={{
              background: "#fff",
              borderRadius: "20px",
              boxShadow: isMobile ? "none" : "0 4px 24px rgba(0,0,0,0.08)",
              p: isMobile ? "0px" : "30px 26px",
              pb: isSmallScreen ? "80px" : undefined,
              minHeight: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: isMobile ? "12px" : isMediumScreen ? "14px" : "24px",
                fontWeight: 700,
                color: "#1D1D1B",
                mb: isMobile ? 1 : "20px",
                lineHeight: isMobile ? "20px" : 1.2,
                letterSpacing: "-0.4px",
              }}
            >
              {vendor?.title || vendor?.cart || "Vendor Product Donation"}
            </Typography>
            {isClosedVendor && (
              <Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 600, color: "#F04951", mb: "12px" }}>
                This vendor is closed for contributions.
              </Typography>
            )}

            <Box
              sx={{
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                border: "0.5px dashed #B9B9B8",
                p: "18px 18px 0 18px",
                mb: 3,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "18px",
              }}
            >
              {products.map((item, idx) => {
                const qty = quantities[item.id] || 0;
                const subtotal = item.unitPrice * qty;
                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      pb: "18px",
                      borderBottom: idx === products.length - 1 ? "none" : "0.8px solid #97979623",
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "8px",
                        background: "transparent",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mr: "16px",
                        overflow: "hidden",
                      }}
                    >
                      {item.image ? <img src={item.image} alt={item.title} width="64" height="64" style={{ objectFit: "cover" }} /> : null}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: "#1D1D1B", fontSize: "14px", mb: "8px" }}>{item.title}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Button onClick={() => updateQty(item.id, -1)} sx={{ minWidth: "24px", width: "24px", height: "24px", p: 0, border: "1px solid #D1DFAD", borderRadius: "6px", color: "#1D1D1B" }}>-</Button>
                        <Typography sx={{ minWidth: "24px", textAlign: "center", fontWeight: 600, color: "#686866", fontSize: "14px" }}>Qty: {qty}</Typography>
                        <Button onClick={() => updateQty(item.id, 1)} sx={{ minWidth: "24px", width: "24px", height: "24px", p: 0, border: "1px solid #D1DFAD", borderRadius: "6px", color: "#1D1D1B" }}>+</Button>
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#1D1D1B" }}>₹{subtotal.toLocaleString("en-IN")}</Typography>
                  </Box>
                );
              })}
            </Box>

            {isInrPayment && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#E8E8E8",
                  padding: "10px 10px 10px 20px",
                  borderRadius: isMobile ? "4px" : "10px",
                  mb: "22px",
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414" }}>
                  Support us by :
                </Typography>
                <select
                  value={supportBy}
                  onChange={(e) => setSupportBy(e.target.value)}
                  style={{
                    minWidth: isMobile ? "110px" : "160px",
                    background: "#fff",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: isMobile ? "10px" : "15px",
                    border: "1.114px solid #B9B9B8",
                    height: isMobile ? "32px" : "40px",
                  }}
                >
                  {supportOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Box>
            )}

            <Box sx={{ border: "1.114px solid #BDD188", background: "#F7F9EC", borderRadius: "8px", px: "18px", py: "13px", mb: "18px", width: "100%" }}>
              {isInrPayment && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "18px" }}>
                    <Typography sx={{ fontSize: isMobile ? "11px" : "14px", color: "#1D1D1B", fontWeight: 600 }}>
                      Tip Amount
                    </Typography>
                    <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#8EA946", fontWeight: 700 }}>
                      ₹{tip.toLocaleString("en-IN")}{" "}
                      <span style={{ color: "#1D1D1B", fontWeight: 600 }}>({tipPercentage}%)</span>
                    </Typography>
                  </Box>
                  <Box sx={{ borderBottom: "1px dashed #B9B9B8", mb: "18px" }} />
                </>
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: !isInrPayment ? "10px" : 0, gap: "8px", flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 700, color: "#1D1D1B", fontSize: isMobile ? "12px" : "14px" }}>
                  Total Amount (INR)
                </Typography>
                <Typography sx={{ fontWeight: 700, color: "#1D1D1B", fontSize: isMobile ? "13px" : "14px" }}>
                  ₹{(isInrPayment ? inrTotalWithTip : inrOverseasBase).toLocaleString("en-IN")}
                </Typography>
              </Box>
              {!isInrPayment && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "10px", gap: "8px" }}>
                  <Typography sx={{ fontWeight: 700, color: "#8EA946", fontSize: isMobile ? "12px" : "14px" }}>
                    You pay
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "#8EA946", fontSize: isMobile ? "13px" : "15px" }}>
                    {conversionLoading ? (
                      <CircularProgress size={14} sx={{ color: "#8EA946" }} />
                    ) : convertedAmount != null ? (
                      formatConvertedAmount(convertedAmount, currency.code)
                    ) : (
                      "—"
                    )}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                  mt: "12px",
                }}
              >
                <Typography sx={{ fontWeight: 600, color: "#1D1D1B", fontSize: isMobile ? "11px" : "13px" }}>
                  Payment currency
                </Typography>
                <select
                  value={currency.code}
                  onChange={(e) => {
                    const next = currencyList.find((c) => c.code === e.target.value);
                    if (next) setCurrency(next);
                  }}
                  style={{
                    minWidth: isMobile ? "120px" : "160px",
                    background: "#fff",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: isMobile ? "11px" : "13px",
                    border: "1.114px solid #B9B9B8",
                    height: isMobile ? "32px" : "36px",
                  }}
                >
                  {currencyList.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code === "INR" ? "INR (India)" : c.code}
                    </option>
                  ))}
                </select>
              </Box>
              {(conversionError || formErrors.conversion) && !isInrPayment && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 1 }}>
                  {conversionError || formErrors.conversion}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "auto" : "auto auto", columnGap: "27px" }}>
              <AuthenticationTextInput
                label="Full Name"
                value={form.name}
                onChange={handleInputChange("name")}
                placeholder="Enter Full Name"
                required
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
              <AuthenticationTextInput
                label="Email ID"
                value={form.email}
                onChange={handleInputChange("email")}
                placeholder="Enter E mail Id"
                type="email"
                required
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
              <AuthenticationTextInput
                label="Phone Number"
                value={form.phone}
                onChange={handleInputChange("phone")}
                placeholder={isInrPayment ? "Enter Phone Number" : "e.g. +1234567890"}
                phoneInput={isInrPayment}
                type={isInrPayment ? "tel" : "text"}
                required
                error={!!formErrors.phone}
                helperText={formErrors.phone}
              />
              {isInrPayment && (
                <AuthenticationTextInput
                  label="Postal Code"
                  value={form.postal}
                  onChange={handleInputChange("postal")}
                  placeholder="Enter Postal Code"
                  required
                  error={!!formErrors.postal}
                  helperText={formErrors.postal}
                />
              )}
              {!isInrPayment && (
                <>
                  <Box sx={{ mb: isMobile ? "20px" : "22px" }}>
                    <Typography
                      sx={{
                        mb: isMobile ? "10px" : "12px",
                        fontWeight: 500,
                        fontSize: isMobile ? "14px" : "16px",
                        color: "#1D1D1B",
                      }}
                    >
                      Country
                    </Typography>
                    <Box sx={{ position: "relative", width: "100%" }}>
                      <select
                        value={form.countryId || ""}
                        onChange={handleCountryChange}
                        style={fieldSelectStyle(isMobile, !!formErrors.country)}
                      >
                        <option value="" disabled>
                          Select Country
                        </option>
                        {Countries.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <Box
                        component="img"
                        src={Images.downArrow}
                        alt=""
                        sx={{
                          position: "absolute",
                          right: isMobile ? "12px" : "18px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: isMobile ? "20px" : "24px",
                          pointerEvents: "none",
                        }}
                      />
                    </Box>
                    {formErrors.country && (
                      <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                        {formErrors.country}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ mb: isMobile ? "20px" : "22px" }}>
                    <Typography
                      sx={{
                        mb: isMobile ? "10px" : "12px",
                        fontWeight: 500,
                        fontSize: isMobile ? "14px" : "16px",
                        color: "#1D1D1B",
                      }}
                    >
                      State
                    </Typography>
                    <Box sx={{ position: "relative", width: "100%" }}>
                      <select
                        value={
                          filteredStates.find((s) => s.code === form.state)?.name ||
                          filteredStates.find((s) => s.name === form.state)?.name ||
                          ""
                        }
                        onChange={handleStateChange}
                        disabled={!form.countryId || filteredStates.length === 0}
                        style={{
                          ...fieldSelectStyle(isMobile, !!formErrors.state),
                          background: !form.countryId || filteredStates.length === 0 ? "#f5f5f5" : "#fff",
                          cursor: !form.countryId || filteredStates.length === 0 ? "not-allowed" : "pointer",
                        }}
                      >
                        <option value="" disabled>
                          {!form.countryId
                            ? "Select Country First"
                            : filteredStates.length === 0
                              ? "No States Available"
                              : "Select State"}
                        </option>
                        {filteredStates.map((s) => (
                          <option key={s.id} value={s.name}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      <Box
                        component="img"
                        src={Images.downArrow}
                        alt=""
                        sx={{
                          position: "absolute",
                          right: isMobile ? "12px" : "18px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: isMobile ? "20px" : "24px",
                          pointerEvents: "none",
                        }}
                      />
                    </Box>
                    {formErrors.state && (
                      <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                        {formErrors.state}
                      </Typography>
                    )}
                  </Box>
                  <AuthenticationTextInput
                    label="Postal Code"
                    value={form.postal}
                    onChange={handleInputChange("postal")}
                    placeholder="Enter Postal Code"
                    required
                    error={!!formErrors.postal}
                    helperText={formErrors.postal}
                  />
                </>
              )}
            </Box>

            <FormControlLabel
              control={<Checkbox checked={anonymousCheck} onChange={(e) => setAnonymousCheck(e.target.checked)} sx={{ color: "#f04951", "&.Mui-checked": { color: "#f04951" }, p: 0, mr: "10px", width: isMobile ? "16px" : "20px" }} />}
              label={<Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 500, color: "#1D1D1B" }}>Make my donation anonymous</Typography>}
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            <FormControlLabel
              control={<Checkbox checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} sx={{ color: "#f04951", "&.Mui-checked": { color: "#f04951" }, p: 0, mr: "10px", width: isMobile ? "16px" : "20px" }} />}
              label={<Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 500, color: "#1D1D1B" }}>I want to receive transaction and donation updates on WhatsApp</Typography>}
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isIndianCitizen}
                  onChange={(e) => setIsIndianCitizen(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 500, color: "#1D1D1B" }}>
                  Are you an Indian citizen ?
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />

            <Typography sx={{ fontSize: isMobile ? "10px" : "14px", color: "#1D1D1B", mb: isMobile ? 2 : 3, fontWeight: 500 }}>
              By continuing, you are agreeing to <span style={{ color: "#FF5858" }}>Terms of Use</span> and <span style={{ color: "#FF5858" }}>Privacy Policy</span>
            </Typography>

            <Box sx={{ width: "100%", display: isSmallScreen ? "none" : "block" }}>
              {stripeClientSecret ? (
                <StripeEmbeddedCheckout clientSecret={stripeClientSecret} onComplete={onStripeComplete} />
              ) : (
                <CustomDonateButton
                  onClick={handleDonateClick}
                  isMobile={isMobile}
                  isTablet={isMediumScreen}
                  disabled={
                    totalQty === 0 ||
                    submitting ||
                    isUnavailableVendor ||
                    (!isInrPayment && (conversionLoading || convertedAmount == null))
                  }
                >
                  {submitting ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                      Processing...
                    </Box>
                  ) : (
                    donateButtonLabel()
                  )}
                </CustomDonateButton>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          position: "fixed",
          backgroundColor: "#fff",
          bottom: 0,
          left: 0,
          padding: "16px 10px",
          boxShadow: "0 -4px 16px 0 rgba(0, 0, 0, 0.10)",
          display: isSmallScreen ? "block" : "none",
          zIndex: 1200,
        }}
      >
        {stripeClientSecret ? (
          <StripeEmbeddedCheckout clientSecret={stripeClientSecret} onComplete={onStripeComplete} />
        ) : (
          <CustomDonateButton
            onClick={handleDonateClick}
            isMobile={isMobile}
            isTablet={isMediumScreen}
            disabled={
              totalQty === 0 ||
              submitting ||
              isUnavailableVendor ||
              (!isInrPayment && (conversionLoading || convertedAmount == null))
            }
          >
            {submitting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                Processing...
              </Box>
            ) : (
              donateButtonLabel()
            )}
          </CustomDonateButton>
        )}
      </Box>
    </Box>
  );
};

export default SmallVendorsPaymentPage;
